import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import googleTestCampaignApi from './testCampaignApi'
import { getPollingInterval, isTerminalState, TestCampaignStatus } from './testCampaignUtils'

const GoogleTestCampaignContext = createContext(null)

export function useGoogleTestCampaignContext() {
  const ctx = useContext(GoogleTestCampaignContext)
  if (!ctx) {
    throw new Error('useGoogleTestCampaignContext must be used within GoogleTestCampaignProvider')
  }
  return ctx
}

const initialSelectedAssets = {
  adAccount: null,
  conversionAction: null,
  youtubeVideo: null,
}

export function GoogleTestCampaignProvider({ children, justSettled, onSettledConsumed }) {
  const [currentCampaign, setCurrentCampaign] = useState(null)
  const [modalOpen, setModalOpen] = useState(false)
  const [modalView, setModalView] = useState('selection')
  const [loading, setLoading] = useState(false)
  const [polling, setPolling] = useState(false)
  const [showAutoPrompt, setShowAutoPrompt] = useState(false)
  const [error, setError] = useState(null)
  const [selectedAssets, setSelectedAssets] = useState(initialSelectedAssets)

  const pollTimerRef = useRef(null)
  const pollStartTimeRef = useRef(null)

  // Show auto-prompt when connection fully settles (account ACTIVE + conversion handled)
  useEffect(() => {
    if (justSettled) {
      setShowAutoPrompt(true)
      onSettledConsumed?.()
    }
  }, [justSettled, onSettledConsumed])

  // Check for an ongoing campaign on mount
  useEffect(() => {
    checkForOngoingCampaign()
  }, [])

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollTimerRef.current) clearTimeout(pollTimerRef.current)
    }
  }, [])

  const checkForOngoingCampaign = useCallback(async () => {
    const { data, error: err } = await googleTestCampaignApi.getOngoingCampaign()
    if (err) {
      console.error('Error checking for ongoing Google campaign:', err)
      return
    }
    if (data?.ongoing_campaign) {
      setCurrentCampaign(data.ongoing_campaign)
      if (!isTerminalState(data.ongoing_campaign.status)) {
        startPolling(data.ongoing_campaign.id)
      }
    }
  }, [])

  const startPolling = useCallback((campaignId) => {
    if (pollTimerRef.current) clearTimeout(pollTimerRef.current)

    setPolling(true)
    pollStartTimeRef.current = Date.now()

    const doPoll = async () => {
      const { data, error: err } = await googleTestCampaignApi.getTestCampaignById(campaignId)

      if (err) {
        console.error('Polling error:', err)
        stopPolling()
        return
      }

      if (data) {
        setCurrentCampaign(data)

        if (isTerminalState(data.status)) {
          stopPolling()
        } else {
          const elapsed = Date.now() - pollStartTimeRef.current
          const delay = getPollingInterval(elapsed)
          pollTimerRef.current = setTimeout(doPoll, delay)
        }
      }
    }

    doPoll()
  }, [])

  const stopPolling = useCallback(() => {
    if (pollTimerRef.current) {
      clearTimeout(pollTimerRef.current)
      pollTimerRef.current = null
    }
    setPolling(false)
    pollStartTimeRef.current = null
  }, [])

  const createCampaign = useCallback(async () => {
    setLoading(true)
    setError(null)

    const payload = { ad_account_id: selectedAssets.adAccount.asset_id }
    if (selectedAssets.conversionAction) {
      payload.conversion_action_id = selectedAssets.conversionAction.asset_id
    }
    if (selectedAssets.youtubeVideo) {
      payload.youtube_video_id = selectedAssets.youtubeVideo.youtube_video_id
    }

    const { data, error: err } = await googleTestCampaignApi.createTestCampaign(payload)

    setLoading(false)

    if (err) {
      setError(err)
      setModalView('error')
      return
    }

    if (data) {
      setCurrentCampaign(data)
      setModalView('progress')
      startPolling(data.id)
    }
  }, [selectedAssets, startPolling])

  const handleCloseAutoPrompt = useCallback(() => setShowAutoPrompt(false), [])

  const handleAcceptAutoPrompt = useCallback(() => {
    setShowAutoPrompt(false)
    setModalOpen(true)
    setModalView('selection')
  }, [])

  const openModal = useCallback(() => {
    setError(null)
    if (currentCampaign && !isTerminalState(currentCampaign.status)) {
      setModalView('progress')
    } else {
      setModalView('selection')
      setSelectedAssets(initialSelectedAssets)
    }
    setModalOpen(true)
  }, [currentCampaign])

  const closeModal = useCallback(() => {
    setModalOpen(false)
    setError(null)
    if (currentCampaign && isTerminalState(currentCampaign.status)) {
      setModalView('selection')
      setSelectedAssets(initialSelectedAssets)
    }
  }, [currentCampaign])

  const selectAsset = useCallback((assetType, asset) => {
    setSelectedAssets((prev) => ({ ...prev, [assetType]: asset }))
  }, [])

  const clearSelection = useCallback(() => setSelectedAssets(initialSelectedAssets), [])

  const retry = useCallback(() => {
    setModalView('selection')
    setError(null)
  }, [])

  const viewPreviousCampaign = useCallback(() => {
    if (!currentCampaign) return
    if (currentCampaign.status === TestCampaignStatus.SUCCESS) {
      setModalView('success')
    } else if (currentCampaign.status === TestCampaignStatus.FAILURE) {
      setModalView('error')
    }
  }, [currentCampaign])

  // Transition modal view as campaign status changes
  useEffect(() => {
    if (!currentCampaign || !modalOpen) return

    if (currentCampaign.status === TestCampaignStatus.SUCCESS && modalView === 'progress') {
      setModalView('success')
    } else if (currentCampaign.status === TestCampaignStatus.FAILURE && modalView === 'progress') {
      setModalView('error')
    } else if (
      currentCampaign.status === TestCampaignStatus.IN_PROGRESS ||
      currentCampaign.status === TestCampaignStatus.PENDING
    ) {
      if (modalView !== 'progress') setModalView('progress')
    }
  }, [currentCampaign, modalOpen, modalView])

  const value = {
    currentCampaign,
    modalOpen,
    modalView,
    loading,
    polling,
    showAutoPrompt,
    error,
    selectedAssets,
    handleCloseAutoPrompt,
    handleAcceptAutoPrompt,
    openModal,
    closeModal,
    selectAsset,
    clearSelection,
    createCampaign,
    retry,
    viewPreviousCampaign,
  }

  return (
    <GoogleTestCampaignContext.Provider value={value}>
      {children}
    </GoogleTestCampaignContext.Provider>
  )
}

GoogleTestCampaignProvider.propTypes = {
  children: PropTypes.node.isRequired,
  justSettled: PropTypes.bool.isRequired,
  onSettledConsumed: PropTypes.func,
}
